import { isLoggedIn, LogSentryException, ServiceError } from "@shapr3d/frontend-lib";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { SyncRequestClient } from "../stores/requestClient";
import {
  DrawingID,
  FileDownloadSchema,
  ProjectID,
  type ProjectRole,
  ProjectRoles,
  RevisionID,
  TeamID,
  WebExportID,
} from "../types";
import { UserID } from "./useAccount";
import { useProjectViewLinkInvitation } from "./useProjectViewLink";

export const webExportQueryKey = ["webExportQueryKey"];

export const WebExportSharedStates = [
  "not_shared",
  "anyone_with_link",
  "team_only",
  "restricted",
] as const;
export type WebExportSharedState = (typeof WebExportSharedStates)[number];

export const WebExportAssignableSharedStates = [
  "anyone_with_link",
  "team_only",
  "restricted",
] as const;
export type WebExportAssignableSharedState = (typeof WebExportAssignableSharedStates)[number];

const WebExportAssignableSharedStateDisplayNames = {
  anyone_with_link: "Anyone with the link",
  team_only: "Members of this team",
  restricted: "Only the people invited",
} as const satisfies Record<WebExportAssignableSharedState, string>;

export const WebExportAssignableSharedStateDisplayName = (
  state: keyof typeof WebExportAssignableSharedStateDisplayNames,
  teamName?: string
) => {
  if (state === "team_only" && teamName) {
    return `Members of “\u200A${teamName}\u200A“`;
  }
  return WebExportAssignableSharedStateDisplayNames[state];
};

export const WebExportAssignableUserRoles = ["commenter", "viewer"] as const;
export type WebExportAssignableUserRole = (typeof WebExportAssignableUserRoles)[number];

const WebExportAssignableUserRolesDisplayNames = {
  commenter: "Commenter",
  viewer: "Viewer",
} as const satisfies Record<WebExportAssignableUserRole, string>;

export const WebExportAssignableUserRolesDisplayName = (role: WebExportAssignableUserRole) => {
  return WebExportAssignableUserRolesDisplayNames[role];
};

const DrawingSchema = z.object({
  drawingId: DrawingID,
  fileType: z.enum(["pdf_v1", "svg_v1"]),
  title: z.string(),
  download: FileDownloadSchema,
});
export type Drawing = z.infer<typeof DrawingSchema>;

const WebExportSchema = z.object({
  webExportId: WebExportID,
  projectId: ProjectID,
  revisionId: RevisionID,
  schemaVersion: z.number(),
  uploadedByUserId: UserID,
  uploadedByDeviceId: z.string(),
  uploadedByPlatform: z.string(),
  appVersion: z.string(),
  fileSize: z.number(),
  createdAt: z.coerce.date(),
  uploadedAt: z.coerce.date().optional(),
  title: z.string(),
  includesMaterials: z.boolean(),
  modelFileDownload: FileDownloadSchema.optional(),
  drawingsDownload: z.array(DrawingSchema).optional(),
});

export type WebExport = z.infer<typeof WebExportSchema>;

const WebExportResponseDataSchema = z.object({
  webExport: WebExportSchema,
  teamId: TeamID,
  sharedState: z.enum(WebExportSharedStates),
  baseRole: z.enum(WebExportAssignableUserRoles),
  userRole: z.enum(ProjectRoles),
});

export type WebExportResponseData = z.infer<typeof WebExportResponseDataSchema>;

const _WebExportDataSchema = WebExportResponseDataSchema.extend({
  isCommentingEnabled: z.boolean(),
});

export type WebExportData = z.infer<typeof _WebExportDataSchema>;

export type WebExportRequestPayload = object;

const getIsCommentingEnabled = ({ userRole }: { userRole: ProjectRole }) => {
  const allowedRoles: ProjectRole[] = ProjectRoles.filter((role) => role != "viewer");

  return allowedRoles.includes(userRole);
};

export const useWebExport = () => {
  const { viewLinkId } = useParams();

  const { enabled: isProjectViewLinkInvitationEnabled } = useProjectViewLinkInvitation();

  const body: WebExportRequestPayload = {};

  return useQuery({
    queryKey: [...webExportQueryKey, viewLinkId],
    queryFn: async ({ signal }) => {
      if (isLoggedIn()) {
        try {
          const response = await SyncRequestClient.authenticatedRequest({
            method: "POST",
            url: `/web/web-exports?v=${viewLinkId}`,
            body,
            abortSignal: signal,
          });
          const json = WebExportResponseDataSchema.parse(await response.json());
          return {
            ...json,
            isCommentingEnabled: getIsCommentingEnabled({
              userRole: json.userRole,
            }),
          };
        } catch (error) {
          if (error instanceof ServiceError) {
            throw error;
          }
          LogSentryException({
            messagePrefix: "Web export download error (authenticated)",
            error,
          });
          throw error;
        }
      }

      try {
        const response = await SyncRequestClient.publicRequest({
          method: "POST",
          url: `/web/web-exports?v=${viewLinkId}`,
          body,
          abortSignal: signal,
        });
        const json = WebExportResponseDataSchema.parse(await response.json());

        return {
          ...json,
          isCommentingEnabled: getIsCommentingEnabled({
            userRole: json.userRole,
          }),
        };
      } catch (error) {
        if (!(error instanceof ServiceError)) {
          LogSentryException({ messagePrefix: "Web export download error (public)", error });
        }

        throw error;
      }
    },
    enabled: Boolean(viewLinkId) && !isProjectViewLinkInvitationEnabled,
  });
};
